import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, FilePlus2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import PageHeader from '../components/PageHeader';

const REASONS = [
  { value: 'drug_court', label: 'Drug Court order' },
  { value: 'parole', label: 'Parole / corrections' },
  { value: 'court_order', label: 'Court order (other)' },
  { value: 'baseline', label: 'Programme baseline' },
  { value: 'random', label: 'Random check' },
  { value: 'other', label: 'Other' },
];

const STATUS_COLOR = {
  pending_doctor: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  completed: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-800',
  cancelled: 'bg-stone-200 text-stone-700',
};

export default function SuperAdminUdsPage() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ user_id: '', assigned_doctor_id: '', reason: 'drug_court', reason_notes: '', court_date: '' });

  const refresh = useCallback(async () => {
    setLoading(true);
    const [rR, uR, dR] = await Promise.all([
      authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/uds/requests`),
      authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`),
      authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/doctors`),
    ]);
    if (rR.ok) setRequests((await rR.json()).requests || []);
    if (uR.ok) {
      const d = await uR.json();
      setUsers(d.users || d || []);
    }
    if (dR.ok) setDoctors(((await dR.json()).doctors || []).filter(d => d.status === 'active'));
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const create = async () => {
    if (!form.user_id || !form.assigned_doctor_id) {
      toast.error('Pick a user AND an approving doctor');
      return;
    }
    const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/uds/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, panel: 'standard_8' }),
    });
    if (r.ok) {
      toast.success('UDS request created · doctor notified');
      setShowCreate(false);
      setForm({ user_id: '', assigned_doctor_id: '', reason: 'drug_court', reason_notes: '', court_date: '' });
      refresh();
    } else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Create failed');
    }
  };

  const cancel = async (req) => {
    if (!window.confirm('Cancel this UDS request?')) return;
    const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/uds/${req.request_id}/cancel`, { method: 'POST' });
    if (r.ok) { toast.success('Cancelled'); refresh(); }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="superadmin-uds-page">
      <PageHeader title="UDS / Pathology Screens" backTo="/superadmin" icon={FileText} />
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-stone-900">Active & historical UDS requests</h2>
              <p className="text-sm text-stone-600">8-panel standard court drug screen.  Doctor signs off → user receives QR → NATA-accredited lab uploads result.</p>
            </div>
            <button
              data-testid="create-uds-btn"
              onClick={() => setShowCreate(s => !s)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-stone-900 text-white rounded-full text-sm font-medium hover:bg-stone-800"
            >
              <FilePlus2 className="w-4 h-4" /> New UDS request
            </button>
          </div>

          {showCreate && (
            <div className="border border-dashed border-stone-300 rounded-xl p-4 mb-5 bg-stone-50 grid sm:grid-cols-2 gap-3" data-testid="uds-create-form">
              <div className="sm:col-span-1">
                <label className="block text-xs font-semibold text-stone-700 uppercase mb-1">User</label>
                <select data-testid="uds-user-select" className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm"
                  value={form.user_id} onChange={e => setForm({ ...form, user_id: e.target.value })}>
                  <option value="">— select user —</option>
                  {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} ({u.email})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-700 uppercase mb-1">Approving doctor</label>
                <select data-testid="uds-doctor-select" className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm"
                  value={form.assigned_doctor_id} onChange={e => setForm({ ...form, assigned_doctor_id: e.target.value })}>
                  <option value="">— select doctor —</option>
                  {doctors.map(d => <option key={d.doctor_id} value={d.doctor_id}>{d.name} {d.region ? `(${d.region})` : ''}</option>)}
                </select>
                {doctors.length === 0 && <p className="text-xs text-rose-600 mt-1">No active doctors.  <button onClick={() => navigate('/superadmin/doctors')} className="underline">Invite one.</button></p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-700 uppercase mb-1">Reason</label>
                <select className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm"
                  value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })}>
                  {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-700 uppercase mb-1">Court date (optional)</label>
                <input type="date" className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm"
                  value={form.court_date} onChange={e => setForm({ ...form, court_date: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-stone-700 uppercase mb-1">Notes for doctor (optional)</label>
                <textarea rows={2} className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm"
                  value={form.reason_notes} onChange={e => setForm({ ...form, reason_notes: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <button data-testid="uds-create-submit" onClick={create}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-full text-sm font-medium hover:bg-emerald-700">
                  Send to doctor for approval
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin inline text-stone-500" /></div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12 text-stone-500" data-testid="no-uds-empty">No UDS requests yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="uds-requests-table">
                <thead className="text-stone-500 uppercase text-xs">
                  <tr className="border-b border-stone-200">
                    <th className="text-left py-2 pr-3">User</th>
                    <th className="text-left py-2 pr-3">Reason</th>
                    <th className="text-left py-2 pr-3">Court date</th>
                    <th className="text-left py-2 pr-3">Doctor</th>
                    <th className="text-left py-2 pr-3">Status</th>
                    <th className="text-left py-2 pr-3">Created</th>
                    <th className="text-right py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(r => (
                    <tr key={r.request_id} className="border-b border-stone-100" data-testid={`uds-row-${r.request_id}`}>
                      <td className="py-3 pr-3 font-medium text-stone-900">{r.user_name || r.user_id}</td>
                      <td className="py-3 pr-3 text-stone-700">{r.reason}</td>
                      <td className="py-3 pr-3 text-stone-700">{r.court_date || '—'}</td>
                      <td className="py-3 pr-3 text-stone-700 text-xs">{(doctors.find(d => d.doctor_id === r.assigned_doctor_id) || {}).name || r.assigned_doctor_id}</td>
                      <td className="py-3 pr-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[r.status] || 'bg-stone-100 text-stone-700'}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="py-3 pr-3 text-xs text-stone-500">{r.created_at?.slice(0, 10)}</td>
                      <td className="py-3 text-right">
                        {!['completed', 'cancelled', 'rejected'].includes(r.status) && (
                          <button onClick={() => cancel(r)}
                            className="px-3 py-1 bg-stone-100 text-stone-700 rounded-full text-xs hover:bg-stone-200">
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
